// Typed API client for the sudhan OCR backend (`document_extraction_services`).
//
// We use a separate axios instance from `services/api.ts` because the OCR
// FastAPI lives on a different upstream than the doit-v2 backend services.
// In dev, vite forwards `/document_extraction` to eroyal.pmdgtech.com; in
// prod the same path is served by the Nginx ingress.
//
// Auth: today the backend gates responses on an `x-user-id` header (UUID).
// We source that UUID from the OIDC user `sub` claim. The Bearer token is
// also attached for forward-compatibility with the JWT migration.

import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

import {
  parseSudhanDate,
  parseVerificationStatus,
  type DashboardInformationResponse,
  type DocumentRecord,
  type DocumentRow,
  type LayoutDetail,
  type LayoutListResponse,
  type AttributeRecord,
  type AttributeSection,
  type LayoutSummary,
  type ManualUploadRequest,
  type ManualUploadResponse,
  type TrackingPageRequest,
  type TrackingPageResponse,
  type TrackingSearchOptions,
  type VerificationData,
} from "../types/documents";

// Hardcoded for now — moves to runtime config when task #10 ships.
const OIDC_STORAGE_KEY =
  "oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app";

// Default app namespace expected by the backend. Override per environment
// via VITE_OCR_APP_NAME if multiple deployments serve the same UI.
const DEFAULT_APP_NAME =
  (import.meta.env.VITE_OCR_APP_NAME as string | undefined) ??
  "Document Extraction";

// app_name the `config_studio/*` endpoints (Layout Config inbox) expect.
// The reference (ocr-ui/modules/document-extraction/tools/index.jsx) sends
// `app_name: 'po-invoice-extraction'` to /config_studio/homepage and to
// /search when table_name is config_studio_tracking. Override via
// VITE_OCR_CONFIG_STUDIO_APP_NAME for deployments that ship a different
// namespace for the layout-config table.
const CONFIG_STUDIO_APP_NAME =
  (import.meta.env.VITE_OCR_CONFIG_STUDIO_APP_NAME as string | undefined) ??
  "po-invoice-extraction";

// Sentinel `layout_id` that tells the backend to auto-detect the layout
// instead of routing through a pre-configured one. Used as the default
// when the caller hasn't picked a specific layout (the "Auto-detect"
// option in the upload dropdown). Override via VITE_OCR_AUTO_LAYOUT_ID
// if the backend ever renames the sentinel.
const AUTO_LAYOUT_ID =
  (import.meta.env.VITE_OCR_AUTO_LAYOUT_ID as string | undefined) ??
  "AUTO_LAYOUT";

// Mongo collection that backs the tracking page (and that the delete /
// verification endpoints reference by name). Override via
// VITE_OCR_TRACKING_TABLE for environments that ship a renamed collection.
const DEFAULT_TRACKING_TABLE =
  (import.meta.env.VITE_OCR_TRACKING_TABLE as string | undefined) ??
  "doc_extraction_tracking";

// Optional environment-level pin for `X-User-Id`. Only set this in QA / CI
// where you want every call from this build to use a fixed identity. In
// normal app use the value comes from the `/document_extraction/account`
// resolve flow (see `resolveAccount` / `useResolveOcrAccount`), and the
// interceptor below only falls back to this pin when the resolve hasn't
// landed yet AND `VITE_OCR_X_USER_ID` is explicitly set. No hard-coded
// default — leaving it empty means "trust the resolve flow".
const PINNED_X_USER_ID =
  (import.meta.env.VITE_OCR_X_USER_ID as string | undefined) ?? "";

// Mongo collection that holds page-level extracted content. The
// select_projection endpoint requires this to fan out per-page data.
// "fullextract_content" matches sudhan's existing record_extraction code path.
// const PROJECTION_COLLECTION =
//   (import.meta.env.VITE_OCR_PROJECTION_COLLECTION as string | undefined) ??
//   "fullextract_content";

// Tenant / org identifier the verification endpoints expect. Override via
// VITE_OCR_ORG_ID for multi-tenant environments.
const DEFAULT_ORG_ID =
  (import.meta.env.VITE_OCR_ORG_ID as string | undefined) ?? "1234";

// Authorization: Bearer <jwt> is the canonical auth path now that sudhan's
// FastAPI runs the doit-realm JWT verifier (auth/jwt_verifier.py) and the
// in-process readis shim derives user_id / group_id from the validated
// claims. The legacy x-user-id header is still attached for backwards
// compatibility — handlers that have been migrated read claims directly,
// older paths still key off the header. Set VITE_OCR_SEND_BEARER=false
// only if you're temporarily talking to a backend that hasn't been
// upgraded yet.
const SEND_BEARER =
  (import.meta.env.VITE_OCR_SEND_BEARER as string | undefined) !== "false";

// All OCR API calls go through the `/dev/ocr-engine` reverse-proxy hop
// (defined in vite.config.ts for dev, deploy/docker/nginx.conf for prod,
// and deploy/k8s/deployment.yaml's Ingress). The upstream eroyal-qa
// FastAPI exposes its handlers under /document_extraction, so the
// resulting full URL is e.g.
//   /dev/ocr-engine/document_extraction/tracking_page
// Override via VITE_OCR_BASE_URL when pointing at a different host
// directly (e.g. a local docker-compose stack).
const baseURL = (import.meta.env.MODE === 'development' ? '/document_extraction' : '/document_extraction');

const ocr: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
});

/**
 * Per-call timeout for LLM-backed endpoints. The script generator and
 * prompt-transform handlers routinely take 3-5 minutes end-to-end
 * because they make upstream calls to gemini / gpt and then post-process
 * the result. The default 30s aborts them client-side, surfacing as
 * `(canceled)` in DevTools. 300s (5 min) gives the backend enough
 * headroom without letting truly stuck calls hang forever. Override
 * per-deployment via VITE_OCR_LLM_TIMEOUT_MS.
 */
const LLM_TIMEOUT_MS = parseInt(
  (import.meta.env.VITE_OCR_LLM_TIMEOUT_MS as string | undefined) ?? "300000",
  10,
);

interface StoredOidcUser {
  access_token?: string;
  profile?: {
    sub?: string;
    email?: string;
    preferred_username?: string;
  };
}

function readStoredUser(): StoredOidcUser | null {
  try {
    const raw = localStorage.getItem(OIDC_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredOidcUser;
  } catch {
    return null;
  }
}

/**
 * Module-level cache of the X-User-Id resolved via /document_extraction/account.
 * Set by `setResolvedXUserId` (called from useResolveOcrAccount) and read by
 * the request interceptor. Lives at module scope (not React state) so the
 * sync interceptor can attach the header without juggling promises.
 */
let _resolvedXUserId: string | null = null;

/** Update the module-level X-User-Id cache. Call after a successful resolve. */
export function setResolvedXUserId(id: string | null): void {
  _resolvedXUserId = id;
}

/** Inspect the cache (useful for debugging — not normally needed in app code). */
export function getResolvedXUserId(): string | null {
  return _resolvedXUserId;
}

ocr.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const user = readStoredUser();

    // Resolution priority for the X-User-Id header:
    //   1. value resolved via /document_extraction/account (per session)
    //   2. explicit pin via VITE_OCR_X_USER_ID (testing / staging)
    //   3. OIDC `sub` claim (fallback for pre-account-flow setups)
    // The /account flow is the canonical path now; the others stay as
    // safety nets so existing dev configs keep working.
    const userId =
      _resolvedXUserId ||
      PINNED_X_USER_ID ||
      user?.profile?.sub ||
      "";
    if (userId) {
      config.headers["X-User-Id"] = userId;
    }

    // The OCR backend currently has its own auth middleware that rejects
    // JWTs minted by other realms with a "session expired" envelope, so we
    // only attach the Bearer token when explicitly opted-in.
    if (SEND_BEARER && user?.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return config;
  },
  (err) => Promise.reject(err),
);

// ---------------------------------------------------------------- helpers

/**
 * Status codes / error markers that warrant a retry. 504 is the
 * specific case we care about — when an upstream proxy times out
 * mid-LLM-call, the backend often finishes the work in the background
 * and a subsequent call returns the cached result. 502 / 503 cover
 * momentary gateway hiccups in the same vein.
 */
const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);

/**
 * Schedule for LLM-call retries. The OCR backend's script-generation
 * endpoints make upstream calls to Gemini/GPT that can run 3-5 minutes
 * end-to-end, but the upstream nginx `proxy_read_timeout` fires at
 * ~60s. The backend continues processing past the 504 and caches the
 * result, so a retry spaced across the expected LLM duration will
 * generally hit the cache.
 *
 * Five 60-second gaps cover the full ~5-minute completion window.
 * Total worst case ~5 min of retries on top of the first 504 (~60s),
 * matching what callers report seeing in production.
 */
const LLM_RETRY_DELAYS_MS = [
  60_000,
  60_000,
  60_000,
  60_000,
  60_000,
];

/**
 * Wrap a long-running call with retries on gateway-timeout-style errors.
 * Default backoff is the LLM schedule above; callers can override with
 * their own delay list. The retries use the same closure so callers
 * don't need to know it's happening — they just see one resolved
 * promise (or the final error).
 */
async function withGatewayRetry<T>(
  fn: () => Promise<T>,
  opts: { delaysMs?: number[] } = {},
): Promise<T> {
  const delays = opts.delaysMs ?? LLM_RETRY_DELAYS_MS;
  let lastErr: unknown;
  // delays.length retries means delays.length + 1 total attempts.
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = axios.isAxiosError(err)
        ? err.response?.status
        : undefined;
      const isRetryable = status && RETRYABLE_STATUS_CODES.has(status);
      if (attempt >= delays.length || !isRetryable) {
        throw err;
      }
      await new Promise((r) => setTimeout(r, delays[attempt]));
    }
  }
  throw lastErr;
}

/** Read a `File` (from <input type="file"> / dropzone) as base64. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("FileReader returned a non-string result"));
        return;
      }
      // Strip the `data:<mime>;base64,` prefix.
      const base64 = result.includes(",") ? result.split(",", 2)[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.readAsDataURL(file);
  });
}

function enrichRow(raw: DocumentRecord): DocumentRow {
  return {
    ...raw,
    verification: parseVerificationStatus(raw.verification_status),
    startedAtIso: parseSudhanDate(raw.start_time),
  };
}

// The backend uses `status` for two completely different shapes:
//   1) success — `status` is an ARRAY of document rows: { status: [...], total_records: N }
//   2) error — `status` is the STRING "success" with a human message:
//      { status: "success", message: "Your session has expired. Please login again." }
// We detect the error envelope before trying to .map() on the value.
interface RawTrackingResponse {
  status?: DocumentRecord[] | string;
  total_records?: number;
  message?: string;
  // Some endpoints might use `data` instead — keep a soft fallback.
  data?: DocumentRecord[];
}

/** Thrown when the OCR backend returns a non-array `status` field. */
export class OcrApiError extends Error {
  readonly raw: unknown;
  constructor(message: string, raw: unknown) {
    super(message);
    this.raw = raw;
    this.name = "OcrApiError";
  }
}

function extractRows(data: RawTrackingResponse): DocumentRecord[] {
  if (Array.isArray(data.status)) return data.status;
  if (Array.isArray(data.data)) return data.data;
  // Error envelope — surface the backend's message.
  if (typeof data.status === "string" && data.message) {
    throw new OcrApiError(data.message, data);
  }
  // Unknown shape — fail loudly rather than silently returning [].
  throw new OcrApiError(
    "Unexpected response shape (no status[] array or message)",
    data,
  );
}

// ---------------------------------------------------------------- api surface

export const ocrApi = {
  /**
   * Resolve a Keycloak {orgId, username} pair into the canonical X-User-Id
   * the OCR backend expects on every subsequent call. The backend creates
   * the user_management row JIT when one doesn't exist yet — so this is
   * also the moment a brand-new doit user gets provisioned on the OCR
   * side. Call once after login (see useResolveOcrAccount).
   *
   * Sends NO `x-user-id` header itself — that would be circular. The
   * interceptor still attaches Bearer when SEND_BEARER is on; otherwise
   * this is an unauthenticated POST relying on network isolation +
   * server-side audit.
   */
  async resolveAccount(req: {
    orgId: string;
    username: string;
    /**
     * Human-readable org name resolved by the caller via the IAM
     * `/organizations/{orgId}` endpoint (see useResolveOcrAccount).
     * Optional -- if the lookup errors or returns an empty name we
     * omit the key from the payload rather than sending an empty
     * string.
     */
    orgName?: string;
  }): Promise<{ xUserId: string }> {
    const payload: Record<string, string> = {
      orgId: req.orgId,
      username: req.username,
    };
    if (req.orgName) {
      payload.org_name = req.orgName;
    }
    const { data } = await ocr.post<Record<string, unknown>>("/account", payload);
    // Canonical success envelope is `{status: "success", user_id: "<id>"}`.
    // We tolerate a few alt key casings so a future rename / additional
    // header-style key doesn't silently break the resolve.
    const id =
      (typeof data["user_id"] === "string" && (data["user_id"] as string)) ||
      (typeof data["X-User-Id"] === "string" && (data["X-User-Id"] as string)) ||
      (typeof data["x-user-id"] === "string" && (data["x-user-id"] as string)) ||
      (typeof data["xUserId"] === "string" && (data["xUserId"] as string)) ||
      "";
    if (!id) {
      // If the backend explicitly signalled a non-success status, surface
      // its message rather than the generic "no X-User-Id" one.
      const status = typeof data["status"] === "string" ? (data["status"] as string) : "";
      const message = typeof data["message"] === "string" ? (data["message"] as string) : "";
      if (status && status !== "success") {
        throw new OcrApiError(
          message || `account resolve failed (status=${status})`,
          data,
        );
      }
      throw new OcrApiError(
        "account/resolve response missing user_id",
        data,
      );
    }
    return { xUserId: id };
  },

  /** Upload a document for extraction. Pushes onto the Kafka pipeline. */
  async manualUpload(req: ManualUploadRequest): Promise<ManualUploadResponse> {
    const { data } = await ocr.post<ManualUploadResponse>("/manual_upload", {
      // When the caller hasn't selected a specific layout, send the
      // `AUTO_LAYOUT` sentinel so the backend runs auto-detect rather
      // than rejecting the upload for a missing layout id.
      layout_id: req.layoutId ?? AUTO_LAYOUT_ID,
      file_name: req.fileName,
      file: req.fileBase64,
      uploader: req.uploader,
      // `app_name` is required by the backend `manualUploadData` pydantic
      // model — omitting it returns 422. Fall back to the module-level
      // default so callers don't have to thread it through every time.
      app_name: req.appName ?? DEFAULT_APP_NAME,
    });
    return data;
  },

  /** Convenience wrapper that handles base64 encoding for you. */
  async uploadFile(opts: {
    file: File;
    layoutId?: string;
    uploader: string;
    /** Override the default app namespace for this single upload. */
    appName?: string;
  }): Promise<ManualUploadResponse> {
    const fileBase64 = await fileToBase64(opts.file);
    return ocrApi.manualUpload({
      layoutId: opts.layoutId,
      fileName: opts.file.name,
      fileBase64,
      uploader: opts.uploader,
      appName: opts.appName,
    });
  },

  /**
   * Layout Config upload — POST /document_extraction/config_studio/manual_upload.
   *
   * Backend payload (pydantic ManualUploadData on the config_studio router):
   *   { file_name, file, uploader, app_name, layout_id }
   *
   * `layout_id` routes the document on the Layout Config side:
   *   - "AUTO_LAYOUT"   → backend auto-detects the layout
   *   - "MANUAL_LAYOUT" → routed to manual layout configuration
   * `app_name` defaults to the config-studio namespace
   * "po-invoice-extraction".
   */
  async toolsDocumentUpload(opts: {
    file: File;
    /**
     * One of "AUTO_LAYOUT" | "MANUAL_LAYOUT". Defaults to "AUTO_LAYOUT"
     * when omitted (matches the upload modal's default selection).
     */
    layoutId?: string;
    uploader: string;
    /** Override the config-studio app namespace for this single upload. */
    appName?: string;
  }): Promise<ManualUploadResponse> {
    const fileBase64 = await fileToBase64(opts.file);
    const { data } = await ocr.post<ManualUploadResponse>(
      "/config_studio/manual_upload",
      {
        file_name: opts.file.name,
        file: fileBase64,
        uploader: opts.uploader,
        app_name: opts.appName ?? CONFIG_STUDIO_APP_NAME,
        layout_id: opts.layoutId ?? "AUTO_LAYOUT",
      },
    );
    return data;
  },

  /**
   * Delete one or more documents (by `process_id`) from the tracking table.
   *
   * Backend route: `POST /document_extraction/tracking_page/delete`
   * Payload:
   *   { process_id: ["<uuid>", ...], table_name: "doc_extraction_tracking" }
   *
   * The endpoint accepts a list, so a single delete and a bulk delete go
   * through the same call. Returns whatever envelope the backend sent so
   * callers can react to a partial-success / message payload.
   */
  async deleteTrackingDocuments(
    processIds: string[],
    opts: { tableName?: string } = {},
  ): Promise<unknown> {
    if (processIds.length === 0) {
      // No-op — let callers fire-and-forget without guarding for empty.
      return { status: "success", message: "No documents selected" };
    }
    const { data } = await ocr.post("/tracking_page/delete", {
      process_id: processIds,
      table_name: opts.tableName ?? DEFAULT_TRACKING_TABLE,
    });
    return data;
  },

  /** List recent documents. Optional filters narrow the result set. */
  async trackingPage(req: TrackingPageRequest = {}): Promise<TrackingPageResponse> {
    // Build the request body — only include filter keys that are set so we
    // don't accidentally over-constrain (e.g. send status: "" → no rows).
    const body: Record<string, unknown> = {
      page_number: req.pageNumber ?? 1,
      records_count: req.recordsCount ?? 50,
      app_name: req.appName ?? DEFAULT_APP_NAME,
    };
    if (req.search?.trim()) body.search = req.search.trim();
    if (req.status && req.status !== "all") body.status = req.status;
    if (req.customer && req.customer !== "all") body.customer_name = req.customer;
    if (req.layoutId && req.layoutId !== "all") body.layout_id = req.layoutId;
    if (req.uploader && req.uploader !== "all") body.uploader = req.uploader;
    if (req.dateFrom) body.date_from = req.dateFrom;
    if (req.dateTo) body.date_to = req.dateTo;

    const { data } = await ocr.post<RawTrackingResponse>("/tracking_page", body);
    const raw = extractRows(data);
    return {
      rows: raw.map(enrichRow),
      totalRecords:
        typeof data.total_records === "number" ? data.total_records : raw.length,
    };
  },

  /**
   * Layout Config inbox source. Mirrors the reference's `toolsHomePage`
   * action (ocr-ui/api-service/document-extraction-client.js#toolsHomePage)
   * which posts to `POST /document_extraction/config_studio/homepage`.
   *
   * Returns the same row shape as `trackingPage` but pulled from the
   * `config_studio_tracking` table — i.e. documents uploaded for layout
   * configuration / tools-view, not the full document-extraction pipeline.
   * Keeping the response identical to `trackingPage` lets the SmartInbox
   * component render either data source with no row-level changes.
   *
   * Body matches the reference exactly — only `{page_number, records_count,
   * app_name}`. The reference uses a separate `/search` endpoint with
   * `table_name: "config_studio_tracking"` when filters are active; we
   * fan out to `configStudioSearch` below in that case rather than
   * piggybacking filters onto this endpoint (the backend's
   * `/config_studio/homepage` doesn't accept them — sending extras can
   * cause 422 / empty responses depending on deployment).
   */
  async configStudioHomepage(req: TrackingPageRequest = {}): Promise<TrackingPageResponse> {
    const body: Record<string, unknown> = {
      page_number: req.pageNumber ?? 1,
      records_count: req.recordsCount ?? 50,
      // Reference always sends "po-invoice-extraction" for the
      // config_studio table — DEFAULT_APP_NAME ("Document Extraction")
      // here would make the backend return an empty page.
      app_name: req.appName ?? CONFIG_STUDIO_APP_NAME,
    };

    const { data } = await ocr.post<RawTrackingResponse>(
      "/config_studio/homepage",
      body,
    );
    // eslint-disable-next-line no-console
    console.log("[ocrApi.configStudioHomepage] raw response", data);
    const raw = extractRows(data);
    return {
      rows: raw.map(enrichRow),
      totalRecords:
        typeof data.total_records === "number" ? data.total_records : raw.length,
    };
  },

  /**
   * Filtered search against the config_studio_tracking table. Mirrors
   * the reference's `fetchSearch` dispatch in tools/index.jsx — single
   * field/value pair scoped by `table_name`. Falls back to the user's
   * search term as a generic "filename" search when no explicit field
   * is provided (matches the reference's default `searchField`).
   */
  async configStudioSearch(req: TrackingPageRequest = {}): Promise<TrackingPageResponse> {
    const term = req.search?.trim() ?? "";
    if (!term) {
      // No term → just hit the unfiltered homepage so the caller doesn't
      // have to special-case the empty-search branch.
      return this.configStudioHomepage(req);
    }
    const body: Record<string, unknown> = {
      field: "filename",
      value: term,
      table_name: "config_studio_tracking",
      page_number: req.pageNumber ?? 1,
      // Same hardcoded namespace as the homepage endpoint above —
      // matches the reference's fetchSearch payload.
      app_name: req.appName ?? CONFIG_STUDIO_APP_NAME,
    };
    const { data } = await ocr.post<RawTrackingResponse>("/search", body);
    const raw = extractRows(data);
    return {
      rows: raw.map(enrichRow),
      totalRecords:
        typeof data.total_records === "number" ? data.total_records : raw.length,
    };
  },

  /**
   * Returns the filter options the inbox should populate dropdowns with.
   * Coerced into a stable shape regardless of envelope variations.
   */
  async trackingSearchOptions(): Promise<TrackingSearchOptions> {
    const { data } = await ocr.post<unknown>("/tracking_search_options", {
      app_name: DEFAULT_APP_NAME,
    });
    return coerceSearchOptions(data);
  },

  /**
   * Dedicated layout-id list for the upload-document dropdown. Mirrors
   * the reference's `fetchTrackingSearchLayoutId`
   * (ocr-ui/.../document-extraction-client.js):
   *
   *   POST /document_extraction/tracking_search_layout_id  {}
   *   →    { status: "success", layout_id: ["john_deere", "JCB", ...] }
   *
   * Distinct from `/tracking_search_options` (which returns the full
   * filter palette — customers, uploaders, layouts, statuses,
   * workflows). The upload dropzone only needs the layout list, so
   * this smaller endpoint is preferred. Tolerates a few response
   * shape variants the reference accepts (array of strings,
   * `{layout_id: [...]}`, or legacy `{status: [...]}` / `{data: [...]}`).
   * `EDATA_CHEQUE` is filtered out — it's a batch-extraction-only
   * sentinel that doesn't belong in the generic upload picker.
   */
  async trackingSearchLayoutId(): Promise<string[]> {
    const { data } = await ocr.post<unknown>(
      "/tracking_search_layout_id",
      {},
    );
    const pickStrings = (arr: unknown[]): string[] =>
      arr
        .map((v) => (typeof v === "string" ? v : null))
        .filter((s): s is string => !!s);

    let layouts: string[] = [];
    if (Array.isArray(data)) {
      layouts = pickStrings(data);
    } else if (data && typeof data === "object") {
      const obj = data as Record<string, unknown>;
      if (Array.isArray(obj.layout_id)) {
        layouts = pickStrings(obj.layout_id);
      } else if (Array.isArray(obj.status)) {
        layouts = pickStrings(obj.status);
      } else if (Array.isArray(obj.data)) {
        layouts = pickStrings(obj.data);
      }
    }
    return layouts.filter((id) => id !== "EDATA_CHEQUE");
  },

  async search(query: { q: string }): Promise<TrackingPageResponse> {
    const { data } = await ocr.post<RawTrackingResponse>("/search", {
      query: query.q,
      app_name: DEFAULT_APP_NAME,
    });
    const raw = extractRows(data);
    return {
      rows: raw.map(enrichRow),
      totalRecords:
        typeof data.total_records === "number" ? data.total_records : raw.length,
    };
  },

  async dashboardInformation(): Promise<DashboardInformationResponse> {
    const { data } = await ocr.post<DashboardInformationResponse>(
      "/dashboard/dashboard_information",
      { app_name: DEFAULT_APP_NAME },
    );
    return data;
  },

  // ------------------------------------------------------------- layouts

  /**
   * List all layouts available for the current app namespace.
   * Backend requires `page_number` + `records_count` on this endpoint
   * even though we usually want everything; default to a generous page.
   */
  async listLayouts(): Promise<LayoutListResponse> {
    const { data } = await ocr.post<unknown>(
      "/management_layout/layout_id_list"
    );

    // Response shapes the backend has returned over time. We handle all of
    // them so the page works on any deployed engine version.
    //
    //  1. Bare array of rows:                  [ {...layout}, ... ]
    //  2. Legacy envelope with rows on `status`:
    //     { status: [ {...layout}, ... ], total_records: N }
    //  3. Current shape — `layout_list` is a MAP of workflow_id → array
    //     of layout_id strings:
    //       { "status": "success",
    //         "layout_list": {
    //           "<workflow_id>": ["layout_a", "layout_b"],
    //           "<workflow_id>": ["layout_c"]
    //         } }
    //  4. Same as (3) but nested one level deeper, e.g. `{data: {layout_list: ...}}`
    //     or a wrapper added by a future ingress / proxy.
    //  5. Error envelope: `{status: "success"|"error", message: "..."}`
    //     surfaces as an OcrApiError.

    /** Recursively locate the `layout_list` map anywhere in the response. */
    const findLayoutList = (
      node: unknown,
      depth = 0,
    ): Record<string, unknown> | null => {
      if (!node || typeof node !== "object" || depth > 4) return null;
      if (Array.isArray(node)) return null;
      const rec = node as Record<string, unknown>;
      if (rec.layout_list && typeof rec.layout_list === "object") {
        return rec.layout_list as Record<string, unknown>;
      }
      for (const v of Object.values(rec)) {
        const hit = findLayoutList(v, depth + 1);
        if (hit) return hit;
      }
      return null;
    };

    let raw: LayoutSummary[] = [];
    let total = 0;

    // eslint-disable-next-line no-console
    console.log("[ocrApi.listLayouts] raw response", data);

    if (Array.isArray(data)) {
      raw = data as LayoutSummary[];
      total = raw.length;
    } else if (data && typeof data === "object") {
      const obj = data as Record<string, unknown>;
      const layoutMap = findLayoutList(obj);

      if (layoutMap) {
        // eslint-disable-next-line no-console
        console.log(
          "[ocrApi.listLayouts] branch: layout_list-map",
          Object.keys(layoutMap),
        );
        for (const [workflowId, layouts] of Object.entries(layoutMap)) {
          if (!Array.isArray(layouts)) continue;
          for (const item of layouts) {
            if (typeof item === "string") {
              raw.push({ layout_id: item, workflow_id: workflowId });
            } else if (item && typeof item === "object") {
              const row = item as LayoutSummary;
              raw.push({
                ...row,
                workflow_id: row.workflow_id ?? workflowId,
              });
            }
          }
        }
        total = raw.length;
      } else if (Array.isArray(obj.status)) {
        raw = obj.status as LayoutSummary[];
        total = typeof obj.total_records === "number"
          ? obj.total_records
          : raw.length;
      } else if (Array.isArray(obj.data)) {
        raw = obj.data as LayoutSummary[];
        total = typeof obj.total_records === "number"
          ? obj.total_records
          : raw.length;
      } else if (typeof obj.status === "string" && obj.message) {
        // Error envelope — surface the backend's own message.
        throw new OcrApiError(String(obj.message), data);
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          "[ocrApi.listLayouts] unrecognised shape, keys:",
          Object.keys(obj),
        );
        // Don't throw — return an empty list so the UI shows "No layouts
        // yet" rather than a red error banner. Easier to recover from.
        return { layouts: [], totalRecords: 0 };
      }
    }

    // eslint-disable-next-line no-console
    console.log("[ocrApi.listLayouts] parsed", {
      count: raw.length,
      total,
      first: raw[0],
    });
    return { layouts: raw, totalRecords: total };
  },

  /**
   * Fetch the full configuration for one layout.
   *
   * The backend's success envelope is:
   *   { status: "success", message: "...fetched successfully", data: {...} }
   * The error envelope (e.g. session expiry) is the SAME shape but without
   * `data`. So we pick `data` whenever it's present, and only throw when
   * there's clearly nothing to show.
   */
  async getLayout(layoutId: string): Promise<LayoutDetail> {
    const { data } = await ocr.post<unknown>(
      "/management_layout/layout_id_select",
      { layout_id: layoutId, app_name: DEFAULT_APP_NAME },
    );
    if (!data || typeof data !== "object") {
      throw new OcrApiError("Unexpected layout detail shape", data);
    }
    const obj = data as Record<string, unknown>;

    // Preferred: success envelope with `data` containing the layout.
    if (obj.data && typeof obj.data === "object") {
      return obj.data as LayoutDetail;
    }

    // Some endpoints wrap a single layout under a `status` array.
    if (Array.isArray(obj.status) && obj.status.length > 0) {
      return obj.status[0] as LayoutDetail;
    }

    // Bare layout (very old shape).
    if (obj.layout_id) {
      return data as LayoutDetail;
    }

    // Nothing usable — surface whatever message the backend gave us.
    if (typeof obj.message === "string" && obj.message) {
      throw new OcrApiError(String(obj.message), data);
    }
    throw new OcrApiError(
      "Layout response did not include `data` or layout fields",
      data,
    );
  },

  async deleteLayout(layoutId: string): Promise<void> {
    // Backend contract (curl):
    //   POST /document_extraction/management_layout/layout_id_delete
    //   { "layout_id": "LAYOUT-12345" }
    await ocr.post("/management_layout/layout_id_delete", {
      layout_id: layoutId,
    });
  },

  /**
   * Create or update a layout. The backend's `layout_id_update` endpoint
   * accepts both — for create, the layout_id is new; for update, it's an
   * existing one. Optional fields are passed through unchanged.
   */
  async upsertLayout(req: {
    layoutId: string;
    customerName?: string;
    workflowId?: string;
    fieldExtractionType?: "rules" | "llm" | "";
    lineitemExtractionType?: "rules" | "llm" | "";
    inputType?: "import" | "export" | "";
    status?: "active" | "inactive";
    certifiedStatus?: boolean;
  }): Promise<unknown> {
    // Backend's `LayoutUpdate` pydantic model expects a single `rule` field
    // wrapping the layout document:
    //   class LayoutUpdate(BaseModel):
    //       rule: dict
    // Sending the layout fields at the top level returns 422.
    const rule: Record<string, unknown> = {
      layout_id: req.layoutId,
      customer_name: req.customerName ?? "",
      workflow_id: req.workflowId ?? "",
      field_extraction_type: req.fieldExtractionType ?? "rules",
      lineitem_extraction_type: req.lineitemExtractionType ?? "rules",
      input_type: req.inputType ?? "",
      status: req.status ?? "active",
      certified_status: req.certifiedStatus ?? false,
      app_name: DEFAULT_APP_NAME,
    };
    const { data } = await ocr.post(
      "/management_layout/layout_id_update",
      { rule },
    );
    return data;
  },

  /**
   * Re-triggers line-item / field extraction for a process using an
   * updated rule set. Mirrors ocr-ui's `retriggerLineItem` action →
   * POST `/verification_retriger_lineitem`. The save-layout endpoint
   * (`/management_layout/layout_id_update`) rejects this payload with
   * 422 because the schemas are different — it expects a flat layout
   * record, not the nested `layout_rule` shape the retrigger endpoint
   * needs.
   */
  async verificationRetriggerLineItem(req: {
    processId: string;
    layoutRule: Record<string, unknown>;
    orgId?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/verification_retriger_lineitem", {
      org_id: req.orgId ?? DEFAULT_ORG_ID,
      process_id: req.processId,
      layout_rule: req.layoutRule,
    });
    return data;
  },

  async assignWorkflowToLayout(req: {
    layoutId: string;
    workflowId: string;
  }): Promise<unknown> {
    const { data } = await ocr.post(
      "/management_layout/assign_wf_layout_id",
      {
        layout_id: req.layoutId,
        workflow_id: req.workflowId,
        app_name: DEFAULT_APP_NAME,
      },
    );
    return data;
  },

  async listWorkflows(): Promise<unknown> {
    const { data } = await ocr.post<unknown>(
      "/management_layout/workflow_list",
      { app_name: DEFAULT_APP_NAME },
    );
    return data;
  },

  /**
   * Returns the dropdown options used by the line-item rule editor —
   * `header_identification`, `footer_identification`,
   * `column_segmentation`, `line_item_splitting`. Mirrors ocr-ui's
   * `fetchConfigOptions` action.
   */
  async fetchRuleDelimiterOptions(): Promise<unknown> {
    const { data } = await ocr.post<unknown>(
      "/management_layout/rule_delimiter_list",
      {},
    );
    return data;
  },

  // -------------------------------------------------------- verification

  /**
   * Load the verification payload for one document.
   *
   * Eroyal's Tools View posts:
   *   { org_id, workflow_id, process_id, uploader,
   *     verification_component: "TV",
   *     table_name: "doc_extraction_tracking" }
   *
   * Response shape:
   *   { pages: { "1": { image_path, image_size }, "2": {...}, ... } }
   *
   * Note: app_name is NOT sent on this endpoint (unlike most others).
   */
  async verificationHome(req: {
    processId: string;
    workflowId: string;
    uploader: string;
    verificationComponent: string;
    tableName: string;
    orgId?: string;
  }): Promise<VerificationData> {
    const { data } = await ocr.post<unknown>("/verification_home", {
      org_id: req.orgId ?? DEFAULT_ORG_ID,
      workflow_id: req.workflowId,
      process_id: req.processId,
      uploader: req.uploader,
      verification_component: req.verificationComponent,
      table_name: req.tableName,
    });
    return data as VerificationData;
  },

  /**
   * Persist verification edits for the current page. Mirrors ocr-ui's
   * `onSave` payload: `{process_id, page_no, collection_name,
   * verification_component, table_name, content: JSON.stringify({...})}`.
   * Same comment as submit/reject — dropped `app_name` since the new
   * backend handlers route on `verification_component` instead.
   */
  async verificationSave(
    processId: string,
    body: Record<string, unknown>,
  ): Promise<unknown> {
    const { data } = await ocr.post("/verification_save", {
      process_id: processId,
      ...body,
    });
    return data;
  },

  /**
   * Finalize a verification round. Mirrors ocr-ui's `verificationSubmit`
   * for both TV and BR (Final Checker) — the BR flow needs
   * `workflow_id`, `verification_component: "BR"`, `table_name:
   * "doc_extraction_tracking"`. The legacy TV cockpit was sending
   * `app_name` which the new backend handlers ignore at best and 422 at
   * worst; we drop it here and let callers thread the right context
   * through `body`.
   */
  async verificationSubmit(
    processId: string,
    body: Record<string, unknown> = {},
  ): Promise<unknown> {
    const { data } = await ocr.post("/verification_submit", {
      process_id: processId,
      ...body,
    });
    return data;
  },

  /**
   * Submit / finalize a Template Engine (config_studio) layout. Mirrors
   * the reference's `toolSubmit` action in
   * ocr-ui/.../document-extraction/tools/ToolsView.jsx — the Proceed
   * button on the Tools cockpit posts to a dedicated endpoint instead
   * of the shared `/verification_submit`:
   *
   *   POST /document_extraction/config_studio/tool_submit
   *   { workflow_id, process_id, verification_component, table_name }
   *
   * `verification_component` is always "TV" for this flow;
   * `table_name` is "config_studio_tracking".
   */
  async configStudioToolSubmit(req: {
    processId: string;
    workflowId: string;
    verificationComponent?: string;
    tableName?: string;
  }): Promise<{ status?: string; message?: string }> {
    const { data } = await ocr.post("/config_studio/tool_submit", {
      workflow_id: req.workflowId,
      process_id: req.processId,
      verification_component: req.verificationComponent ?? "TV",
      table_name: req.tableName ?? "config_studio_tracking",
    });
    return data as { status?: string; message?: string };
  },

  /**
   * Reject the current verification round. Mirrors the reference's
   * `onReject` payload: `{process_id, verification_component, table_name}`.
   * Backward-compatible for the legacy reason argument — if either body
   * is empty (string) or omitted, no extra fields are sent and the
   * backend handles it as a bare rejection.
   */
  async verificationReject(
    processId: string,
    reasonOrBody?: string | Record<string, unknown>,
  ): Promise<unknown> {
    const body: Record<string, unknown> = { process_id: processId };
    if (typeof reasonOrBody === "string") {
      if (reasonOrBody.length > 0) body.reason = reasonOrBody;
    } else if (reasonOrBody && typeof reasonOrBody === "object") {
      Object.assign(body, reasonOrBody);
    }
    const { data } = await ocr.post("/verification_reject", body);
    return data;
  },

  /**
   * Resets the current page's extracted content. Mirrors ocr-ui's
   * `verificationReset` payload:
   *   { process_id, page_no, collection_name, verification_component, table_name }
   * The simpler `{ process_id, app_name }` shape returns 422.
   */
  async verificationReset(req: {
    processId: string;
    collectionName: string;
    pageNumber: number | string;
    verificationComponent?: string;
    tableName?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/verification_reset", {
      process_id: req.processId,
      page_no: String(req.pageNumber),
      collection_name: req.collectionName,
      verification_component: req.verificationComponent ?? "TV",
      table_name: req.tableName ?? "doc_extraction_tracking",
    });
    return data;
  },

  /**
   * Clears the current page's extracted content for the given collection.
   * Mirrors ocr-ui's `verificationClear` action — the backend expects:
   *   { process_id, verification_component, collection_name, page_no }
   * (no app_name, no `clear` flag — that variant returns 422.)
   */
  async verificationClear(req: {
    processId: string;
    collectionName: string;
    pageNumber: number | string;
    verificationComponent?: string;
    tableName?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/verification_clear", {
      process_id: req.processId,
      verification_component: req.verificationComponent ?? "TV",
      collection_name: req.collectionName,
      page_no: String(req.pageNumber),
      table_name: req.tableName ?? "doc_extraction_tracking",
    });
    return data;
  },

  /**
   * Dedicated delete endpoint for individual field items. The backend
   * (eroyal) accepts a `field_name` array and removes those entries
   * from the page's collection in one call. Avoids the "save modified
   * content" workaround that previously required us to walk the
   * extracted_content tree client-side and re-stringify.
   *
   *   POST /document_extraction/verification/fielditem/delete
   *   { process_id, page_no, collection_name, field_name: string[],
   *     verification_component }
   */
  async deleteFieldItem(req: {
    processId: string;
    pageNumber: number | string;
    collectionName: string;
    fieldNames: string[];
    verificationComponent?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/verification/fielditem/delete", {
      process_id: req.processId,
      page_no: String(req.pageNumber),
      collection_name: req.collectionName,
      field_name: req.fieldNames,
      verification_component: req.verificationComponent ?? "TV",
    });
    return data;
  },

  /**
   * Delete an entire line-item table from a page. Mirrors ocr-ui's
   * `lineItemDelete` action with `record_instance: 'table'`:
   *
   *   POST /document_extraction/verification/lineitem/delete
   *   {
   *     process_id, page_no, collection_name,
   *     record_instance: "table",
   *     verification_component,
   *     delete_row_index: [], delete_column_names: [],
   *     table_index, table_name, delete_table_name: `table_${tableIndex + 1}`
   *   }
   */
  async deleteLineItemTable(req: {
    processId: string;
    pageNumber: number | string;
    collectionName: string;
    tableIndex: number;
    verificationComponent?: string;
    tableName?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/verification/lineitem/delete", {
      process_id: req.processId,
      page_no: String(req.pageNumber),
      collection_name: req.collectionName,
      record_instance: "table",
      verification_component: req.verificationComponent ?? "TV",
      delete_row_index: [],
      delete_column_names: [],
      table_index: req.tableIndex,
      table_name: req.tableName ?? "doc_extraction_tracking",
      delete_table_name: `table_${req.tableIndex + 1}`,
    });
    return data;
  },

  /**
   * Companion call the eroyal Tools View makes alongside verification_home.
   * Returns the page projection — page image, OCR text, page count, etc.
   *
   * Note the backend uses `page_no` (not `page_number`) and requires a
   * `collection_name` which points at the Mongo collection holding the
   * extracted content. Defaults to `fullextract_content` — override via
   * the `VITE_OCR_PROJECTION_COLLECTION` env var.
   */
  /**
   * Eroyal Tools View posts:
   *   { org_id, collection_name, process_id, page_no,
   *     verification_component: "TV", table_name: "doc_extraction_tracking" }
   *
   * `collection_name` decides which content body comes back:
   *   "lineitem_content" → table extraction (LINE ITEM tab)
   *   "fielditem_content" → header-field extraction (FIELD ITEM tab)
   */
  async selectProjection(req: {
    processId: string;
    collectionName: string;
    pageNumber?: number;
    verificationComponent?: string;
    tableName?: string;
    orgId?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/select_projection", {
      org_id: req.orgId ?? DEFAULT_ORG_ID,
      collection_name: req.collectionName,
      process_id: req.processId,
      // page_no is sent as a string per the backend pydantic schema.
      page_no: String(req.pageNumber ?? 1),
      verification_component: req.verificationComponent ?? "TV",
      table_name: req.tableName ?? "doc_extraction_tracking",
    });
    return data;
  },

  /**
   * The Tools View fires this twice — once for the FIELD ITEM tab and
   * once for LINE ITEM. Returns the list of identifier attributes used
   * to render the editable extracted-fields tables.
   *
   * `section_type` is required by the backend; if you don't pass one we
   * default to the same value as `verification_component`.
   */
  /**
   * Returns a flat list of field-name suggestions used by the
   * "New custom info" autocomplete in the Tools View — not extracted
   * values. The eroyal endpoint only requires `section_type`:
   *   "field_item" → header-field name suggestions
   *   "line_item"  → line-item column-name suggestions
   *
   * Response shape: `{ status, message, data: string[] }`.
   */
  async fieldsAttributeIdentifierList(req: {
    sectionType: "field_item" | "line_item";
  }): Promise<unknown> {
    const { data } = await ocr.post("/fields_attribute_identifier_list", {
      section_type: req.sectionType,
    });
    return data;
  },

  // ----------------------------------------------------------- attribute CRUD

  /**
   * List configured attributes for the Layout Config "Attributes" screen.
   * Mirrors the reference's `fetchLineItemAttributeList` /
   * `fetchFieldItemAttributeList` (ocr-ui document-extraction-client.js)
   * — the request is an empty POST body; the backend keys off the
   * `X-User-Id` header attached by the request interceptor.
   *
   *   POST /document_extraction/lineitem_attribute_list  {}
   *   POST /document_extraction/fielditem_attribute_list {}
   *
   * Backend envelope: `{status: "success", data: AttributeRecord[]}`. On
   * `{status: "failed", data: []}` we surface an explicit error so the
   * UI can show a retry banner instead of silently rendering "empty".
   */
  async listAttributes(section: AttributeSection): Promise<AttributeRecord[]> {
    const path =
      section === "line_item"
        ? "/lineitem_attribute_list"
        : "/fielditem_attribute_list";
    const { data } = await ocr.post<unknown>(path, {});
    if (Array.isArray(data)) return data as AttributeRecord[];
    if (data && typeof data === "object") {
      const obj = data as Record<string, unknown>;
      if (Array.isArray(obj.data)) return obj.data as AttributeRecord[];
      if (Array.isArray(obj.status)) return obj.status as AttributeRecord[];
      // {status: "failed", ...} — surface as an error so the UI knows.
      if (typeof obj.status === "string" && obj.status !== "success") {
        throw new OcrApiError(
          (typeof obj.message === "string" && obj.message) ||
          `Attribute list returned status="${obj.status}"`,
          data,
        );
      }
    }
    return [];
  },

  /**
   * Insert a new attribute. Mirrors the reference payload exactly:
   *   { status, identifier, type, pattern, description }
   * plus `app_name` so the row lands in the same namespace the list
   * endpoint reads from.
   */
  async insertAttribute(
    section: AttributeSection,
    payload: {
      identifier: string;
      type: string;
      pattern: string;
      description: string;
      status: string;
    },
  ): Promise<{ status?: string; message?: string }> {
    const path =
      section === "line_item"
        ? "/lineitem_attribute_insert"
        : "/fielditem_attribute_insert";
    const { data } = await ocr.post(path, {
      ...payload,
      app_name: CONFIG_STUDIO_APP_NAME,
    });
    return data as { status?: string; message?: string };
  },

  /**
   * Update an existing attribute. Same payload as insert, plus `id`.
   *   POST /lineitem_attribute_update | /fielditem_attribute_update
   */
  async updateAttribute(
    section: AttributeSection,
    payload: {
      id: string;
      identifier: string;
      type: string;
      pattern: string;
      description: string;
      status: string;
    },
  ): Promise<{ status?: string; message?: string }> {
    const path =
      section === "line_item"
        ? "/lineitem_attribute_update"
        : "/fielditem_attribute_update";
    const { data } = await ocr.post(path, {
      ...payload,
      app_name: CONFIG_STUDIO_APP_NAME,
    });
    return data as { status?: string; message?: string };
  },

  /**
   * Delete an attribute. Same endpoint for both line- and field-item, with
   * `field_type` selecting the table.
   *   POST /config_attribute_delete  { id, field_type, app_name }
   */
  async deleteAttribute(
    section: AttributeSection,
    id: string,
  ): Promise<{ status?: string; message?: string }> {
    const { data } = await ocr.post("/config_attribute_delete", {
      id,
      field_type: section,
      app_name: CONFIG_STUDIO_APP_NAME,
    });
    return data as { status?: string; message?: string };
  },

  /**
   * Check whether a given field identifier already exists in the table.
   * Used by the Add/Edit modal to gate the Save button with a
   * debounced uniqueness check.
   *   POST /fields_identifier_check_exists  { section_type, identifier }
   * Returns `{message: "...exists"|"...not exist"}` per the reference.
   */
  async checkAttributeIdentifierExists(req: {
    section: AttributeSection;
    identifier: string;
  }): Promise<{ exists: boolean; raw: unknown }> {
    const { data } = await ocr.post<{ message?: string }>(
      "/fields_identifier_check_exists",
      {
        section_type: req.section,
        identifier: req.identifier,
      },
    );
    const msg = (data?.message ?? "").toLowerCase();
    return {
      exists: msg.includes("exist") && !msg.includes("not exist"),
      raw: data,
    };
  },

  /**
   * Returns the per-field bounding boxes for the active layout. Powers the
   * numbered markers shown on the document image alongside each detected
   * field/cell. Response shape:
   *   { field_item_raw_coordinates: [{ key, value, field_name, field_keywords }] }
   *   or
   *   { line_item_raw_coordinates: [...] }
   * where each `key` and `value` is `{xmin, ymin, xmax, ymax}`.
   */
  async selectCoordinates(req: {
    coordinatesType: "fielditem" | "lineitem";
    layoutId: string;
    pageNumber?: number;
  }): Promise<unknown> {
    const { data } = await ocr.post("/select_coordinates", {
      coordinates_type: req.coordinatesType,
      layout_id: req.layoutId,
      page_no: String(req.pageNumber ?? 1),
    });
    return data;
  },

  // ------------------------------------------------------------- tools manual / automatic

  async manualFieldItem(req: {
    processId: string;
    pages: string; // JSON stringified array of {page_no, boundary}
    tableName: string;
    orgId?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/tools/manual/fielditem", {
      org_id: req.orgId ?? DEFAULT_ORG_ID,
      process_id: req.processId,
      pages: req.pages,
      table_name: req.tableName,
    });
    return data;
  },

  async manualLineItem(req: {
    processId: string;
    pageNo: string;
    boundaries: string; // JSON stringified table boundaries
    tableName: string;
    orgId?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/tools/manual/lineitem", {
      org_id: req.orgId ?? DEFAULT_ORG_ID,
      process_id: req.processId,
      page_no: req.pageNo,
      boundaries: req.boundaries,
      table_name: req.tableName,
    });
    return data;
  },

  async automaticFieldItem(req: {
    processId: string;
    pageNo: number;
    modelName: string;
    tableName: string;
    orgId?: string;
    userPrompt?: string;
    generateRule?: boolean;
  }): Promise<unknown> {
    const { data } = await ocr.post("/tools/automatic/fielditem", {
      org_id: req.orgId ?? DEFAULT_ORG_ID,
      process_id: req.processId,
      page_no: req.pageNo,
      model_name: req.modelName,
      table_name: req.tableName,
      user_prompt: req.userPrompt ?? "",
      generate_rule: req.generateRule ?? true,
    });
    return data;
  },

  async automaticLineItem(req: {
    processId: string;
    pageNo: number;
    modelName: string;
    tableName: string;
    orgId?: string;
    userPrompt?: string;
    generateRule?: boolean;
  }): Promise<unknown> {
    const { data } = await ocr.post("/tools/automatic/lineitem", {
      org_id: req.orgId ?? DEFAULT_ORG_ID,
      process_id: req.processId,
      page_no: req.pageNo,
      model_name: req.modelName,
      table_name: req.tableName,
      user_prompt: req.userPrompt ?? "",
      generate_rule: req.generateRule ?? true,
    });
    return data;
  },

  // ------------------------------------------------------------- business rules / config studio

  /**
   * Fetch business configuration data including extracted line items and field items.
   */
  async fetchBusinessConfiguration(req: {
    processId: string;
    tableName: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/business_configuration/home_page", {
      process_id: req.processId,
      table_name: req.tableName,
    });
    return data;
  },

  /**
   * Execute prompt-based transformation for line items and field items.
   */
  async executePrompt(req: {
    processId: string;
    layoutId: string;
    lineItemPrompt: string;
    fieldItemPrompt: string;
  }): Promise<unknown> {
    return withGatewayRetry(async () => {
      const { data } = await ocr.post(
        "/business_configuration/transform_prompt",
        {
          process_id: req.processId,
          layout_id: req.layoutId,
          business_rules_type: "prompt",
          line_item_prompt: req.lineItemPrompt,
          field_item_prompt: req.fieldItemPrompt,
          use_prompt: "true",
        },
        { timeout: LLM_TIMEOUT_MS },
      );
      return data;
    });
  },

  /**
   * Generate a Python script from expected output data.
   */
  async generateScript(req: {
    processId: string;
    layoutId: string;
    expectedOutput: string;
    expectedOutputFileName?: string;
  }): Promise<unknown> {
    return withGatewayRetry(async () => {
      const { data } = await ocr.post(
        "/business_configuration/generate_script",
        {
          process_id: req.processId,
          layout_id: req.layoutId,
          business_rules_type: "TV",
          expected_output: req.expectedOutput,
          expected_output_file_name:
            req.expectedOutputFileName ?? "extracted_data.json",
        },
        { timeout: LLM_TIMEOUT_MS },
      );
      return data;
    });
  },

  /**
   * Execute a script (Python or other) for business rules transformation.
   *
   * Two payload shapes are accepted by the backend, depending on the flow:
   *
   * 1. **Manual tab** (`businessRulesType = "automatic"`) — runs a
   *    standalone script. Only the script + process/layout context is
   *    required.
   *
   * 2. **Semi-automatic tab** (`businessRulesType = "TV"`) — requires
   *    `expected_output` + `expected_output_file_name` alongside the
   *    script. Mirrors ocr-ui's SemiAutomaticTab which spreads the
   *    generateScript payload into executeScript. Omitting these in TV
   *    mode causes the backend to reject the call with 422.
   */
  async executeScript(req: {
    processId: string;
    layoutId: string;
    script: string;
    scriptType?: string;
    businessRulesType?: string;
    /** Required when businessRulesType === "TV". Base64-encoded JSON. */
    expectedOutput?: string;
    /** Required when businessRulesType === "TV". Default "extracted_data.json". */
    expectedOutputFileName?: string;
  }): Promise<unknown> {
    const body: Record<string, unknown> = {
      process_id: req.processId,
      layout_id: req.layoutId,
      business_rules_type: req.businessRulesType ?? "automatic",
      manual_python_script: req.script,
      script_type: req.scriptType ?? "python",
      use_prompt: "false",
    };
    if (req.expectedOutput !== undefined) {
      body.expected_output = req.expectedOutput;
    }
    if (req.expectedOutputFileName !== undefined) {
      body.expected_output_file_name = req.expectedOutputFileName;
    } else if (req.expectedOutput !== undefined) {
      // Backend requires the filename whenever expected_output is set —
      // mirror the reference's default rather than letting the call fail.
      body.expected_output_file_name = "extracted_data.json";
    }
    return withGatewayRetry(async () => {
      const { data } = await ocr.post(
        "/business_configuration/execute_script",
        body,
        { timeout: LLM_TIMEOUT_MS },
      );
      return data;
    });
  },

  /**
   * Save business rules configuration.
   */
  async saveBusinessRules(req: {
    processId: string;
    layoutId: string;
    businessRulesType: string;
    lineItemPrompt?: string;
    fieldItemPrompt?: string;
    script?: string;
    scriptType?: string;
    saveRules?: boolean;
  }): Promise<unknown> {
    const { data } = await ocr.post("/business_configuration/save_rules", {
      process_id: req.processId,
      layout_id: req.layoutId,
      business_rules_type: req.businessRulesType,
      line_item_prompt: req.lineItemPrompt ?? "",
      field_item_prompt: req.fieldItemPrompt ?? "",
      manual_python_script: req.script ?? "",
      script_type: req.scriptType ?? "python",
      use_prompt: req.businessRulesType === "prompt" ? "true" : "false",
      save_rules: req.saveRules ?? true,
    });
    return data;
  },

  /** POST /export_json — returns the full extracted JSON for a process. */
  async exportJson(processId: string): Promise<unknown> {
    const { data } = await ocr.post("/export_json", { process_id: processId });
    return data;
  },

  /**
   * POST /tracking_page/export — bulk export of tracking-page records.
   * Used by the Final Checker's JSON download button. The backend takes
   * lists of process and workflow IDs (so the same call can later cover
   * multi-document export) plus the tracking collection name.
   */
  async exportTrackingPage(req: {
    processIds: string[];
    workflowIds: string[];
    tableName?: string;
  }): Promise<unknown> {
    const { data } = await ocr.post("/tracking_page/export", {
      process_id: req.processIds,
      workflow_id: req.workflowIds,
      table_name: req.tableName ?? "doc_extraction_tracking",
    });
    return data;
  },

};

/**
 * Coerce the tracking_search_options response into stable string arrays.
 *
 * The backend has shipped a few shapes over time; we try each in order:
 *   1. Current canonical shape — auto_suggestion bucket per field:
 *        { search_options: [...field_names],
 *          auto_suggestion: { layout_id: [...], customer_name: [...], ... } }
 *   2. `data` envelope wrapping (1) or (3):
 *        { data: { auto_suggestion: {...} } } or { data: { layout_id: [...] } }
 *   3. Flat arrays at the top level (legacy):
 *        { layout_id: [...], customer_name: [...], ... }
 *
 * We always de-dupe so the dropdowns don't render the same value twice.
 */
function coerceSearchOptions(raw: unknown): TrackingSearchOptions {
  const empty: TrackingSearchOptions = {
    customers: [],
    layouts: [],
    uploaders: [],
    statuses: [],
    workflows: [],
  };
  if (!raw || typeof raw !== "object") return empty;
  const obj = raw as Record<string, unknown>;
  // Unwrap a top-level `data` envelope if present.
  const root =
    obj.data && typeof obj.data === "object"
      ? (obj.data as Record<string, unknown>)
      : obj;
  // Prefer `auto_suggestion.<field>` (current shape) over a flat top-level
  // array (legacy). Either way we end up with the same string[] per field.
  const suggestionBucket =
    root.auto_suggestion && typeof root.auto_suggestion === "object"
      ? (root.auto_suggestion as Record<string, unknown>)
      : root;

  const pickStrings = (...keys: string[]): string[] => {
    for (const k of keys) {
      const v = suggestionBucket[k];
      if (Array.isArray(v)) {
        const out: string[] = [];
        const seen = new Set<string>();
        for (const item of v) {
          const s =
            typeof item === "string"
              ? item
              : typeof (item as { value?: unknown })?.value === "string"
                ? String((item as { value: string }).value)
                : null;
          if (s && !seen.has(s)) {
            seen.add(s);
            out.push(s);
          }
        }
        return out;
      }
    }
    return [];
  };

  return {
    customers: pickStrings("customer_name", "customers", "customer"),
    layouts: pickStrings("layout_id", "layouts", "layout"),
    uploaders: pickStrings("uploader", "uploaders"),
    statuses: pickStrings("status", "statuses"),
    workflows: pickStrings("workflow_id", "workflows", "workflow"),
  };
}

/**
 * Generic backend envelope unwrap. Returns `data.data` when the backend
 * uses { status: "success", message, data } shape, otherwise returns the
 * raw body. Throws OcrApiError if there's clearly nothing usable.
 */
/**
 * Generic backend envelope unwrap. Returns `data.data` when the backend
 * uses { status: "success", message, data } shape, otherwise returns the
 * raw body. Throws OcrApiError if there's clearly nothing usable.
 */
// function unwrapEnvelope<T>(data: unknown): T {
//   if (!data || typeof data !== "object") {
//     throw new OcrApiError("Unexpected response (not an object)", data);
//   }
//   const obj = data as Record<string, unknown>;
//   if (obj.data && typeof obj.data === "object") {
//     return obj.data as T;
//   }
//   if (typeof obj.message === "string" && !Object.keys(obj).some((k) => k === "data")) {
//     // Pure message envelope without data -- likely an error
//     if (obj.status === "success" || obj.status === undefined) {
//       // success-typed envelope but no data; surface message as error
//       throw new OcrApiError(String(obj.message), data);
//     }
//   }
//   return data as T;
// }

// Re-export the raw axios instance for the rare call site that needs it.
export { ocr as ocrAxios };
